/**
 * Product catalogue vocabulary shared by the API and the web client.
 *
 * The optical domain is the reason `Product` stays generic while each type carries its own detail
 * shape: a frame is described by colour and millimetre measurements, a prescription lens by power
 * ranges and refractive index, a contact lens by base curve and replacement schedule. Forcing all
 * three into one flat row would mean a table of mostly-null columns nobody can validate.
 */

export const ProductType = {
  FRAME: 'FRAME',
  SUNGLASSES: 'SUNGLASSES',
  PRESCRIPTION_LENS: 'PRESCRIPTION_LENS',
  CONTACT_LENS: 'CONTACT_LENS',
  SOLUTION: 'SOLUTION',
  ACCESSORY: 'ACCESSORY',
} as const;
export type ProductType = (typeof ProductType)[keyof typeof ProductType];

/** Types whose variants carry frame measurements. */
export const FRAME_LIKE_TYPES: readonly ProductType[] = [ProductType.FRAME, ProductType.SUNGLASSES];

/** Whether a frame-like product comes with lenses fitted. Only FRAME and SUNGLASSES carry it; no optical-order rule reads it. */
export const LensInclusion = {
  WITHOUT_LENSES: 'WITHOUT_LENSES',
  WITH_LENSES: 'WITH_LENSES',
} as const;
export type LensInclusion = (typeof LensInclusion)[keyof typeof LensInclusion];

export const ProductStatus = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;
export type ProductStatus = (typeof ProductStatus)[keyof typeof ProductStatus];

// ---------------------------------------------------------------------------
// Frames and sunglasses
// ---------------------------------------------------------------------------

export const FrameShape = {
  ROUND: 'ROUND',
  SQUARE: 'SQUARE',
  RECTANGLE: 'RECTANGLE',
  OVAL: 'OVAL',
  AVIATOR: 'AVIATOR',
  CAT_EYE: 'CAT_EYE',
  WAYFARER: 'WAYFARER',
  WRAP: 'WRAP',
  OTHER: 'OTHER',
} as const;
export type FrameShape = (typeof FrameShape)[keyof typeof FrameShape];

export const FrameMaterial = {
  ACETATE: 'ACETATE',
  METAL: 'METAL',
  TITANIUM: 'TITANIUM',
  STAINLESS_STEEL: 'STAINLESS_STEEL',
  TR90: 'TR90',
  COMBINATION: 'COMBINATION',
  WOOD: 'WOOD',
  OTHER: 'OTHER',
} as const;
export type FrameMaterial = (typeof FrameMaterial)[keyof typeof FrameMaterial];

export const FrameRimType = {
  FULL_RIM: 'FULL_RIM',
  SEMI_RIMLESS: 'SEMI_RIMLESS',
  RIMLESS: 'RIMLESS',
} as const;
export type FrameRimType = (typeof FrameRimType)[keyof typeof FrameRimType];

export const TargetGroup = {
  MEN: 'MEN',
  WOMEN: 'WOMEN',
  UNISEX: 'UNISEX',
  KIDS: 'KIDS',
} as const;
export type TargetGroup = (typeof TargetGroup)[keyof typeof TargetGroup];

// ---------------------------------------------------------------------------
// Prescription lenses
// ---------------------------------------------------------------------------

export const LensType = {
  SINGLE_VISION: 'SINGLE_VISION',
  BIFOCAL: 'BIFOCAL',
  PROGRESSIVE: 'PROGRESSIVE',
  OFFICE: 'OFFICE',
  READING: 'READING',
} as const;
export type LensType = (typeof LensType)[keyof typeof LensType];

export const LensMaterial = {
  CR39: 'CR39',
  POLYCARBONATE: 'POLYCARBONATE',
  TRIVEX: 'TRIVEX',
  HIGH_INDEX: 'HIGH_INDEX',
  GLASS: 'GLASS',
} as const;
export type LensMaterial = (typeof LensMaterial)[keyof typeof LensMaterial];

/** Whether a prescription lens is kept on the shelf or made for each job. A made-to-order lens never holds stock. */
export const PrescriptionLensSupplyMode = {
  STOCKED: 'STOCKED',
  MADE_TO_ORDER: 'MADE_TO_ORDER',
} as const;
export type PrescriptionLensSupplyMode =
  (typeof PrescriptionLensSupplyMode)[keyof typeof PrescriptionLensSupplyMode];

// ---------------------------------------------------------------------------
// Contact lenses
// ---------------------------------------------------------------------------

export const ContactLensType = {
  SPHERICAL: 'SPHERICAL',
  TORIC: 'TORIC',
  MULTIFOCAL: 'MULTIFOCAL',
  COLORED: 'COLORED',
} as const;
export type ContactLensType = (typeof ContactLensType)[keyof typeof ContactLensType];

export const ReplacementSchedule = {
  DAILY: 'DAILY',
  BIWEEKLY: 'BIWEEKLY',
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  YEARLY: 'YEARLY',
} as const;
export type ReplacementSchedule = (typeof ReplacementSchedule)[keyof typeof ReplacementSchedule];

// ---------------------------------------------------------------------------
// Barcodes
// ---------------------------------------------------------------------------

export const BarcodeType = {
  EAN_13: 'EAN_13',
  UPC_A: 'UPC_A',
  /** Generated by OptiFlow. Never presented as a globally assigned EAN or UPC. */
  CODE_128: 'CODE_128',
  OTHER: 'OTHER',
} as const;
export type BarcodeType = (typeof BarcodeType)[keyof typeof BarcodeType];

/** What a product photograph shows. A gallery label only; no role is required. */
export const ProductMediaRole = {
  FRONT: 'FRONT',
  SIDE: 'SIDE',
  ADDITIONAL: 'ADDITIONAL',
} as const;
export type ProductMediaRole = (typeof ProductMediaRole)[keyof typeof ProductMediaRole];

/** Photograph upload limits the web app checks before sending; the API enforces them again. */
export const PRODUCT_MEDIA_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

// ---------------------------------------------------------------------------
// Read models
// ---------------------------------------------------------------------------

export interface BrandSummary {
  id: string;
  name: string;
  isActive: boolean;
  productCount?: number;
}

export interface FrameDetails {
  colorName: string | null;
  colorCode: string | null;
  lensWidthMm: number | null;
  bridgeWidthMm: number | null;
  templeLengthMm: number | null;
  shape: FrameShape | null;
  material: FrameMaterial | null;
  rimType: FrameRimType | null;
  targetGroup: TargetGroup | null;
  isPolarized: boolean;
  lensColor: string | null;
}

export interface PrescriptionLensDetails {
  lensType: LensType;
  material: LensMaterial;
  supplyMode: PrescriptionLensSupplyMode;
  refractiveIndex: number | null;
  sphereMin: number | null;
  sphereMax: number | null;
  cylinderMin: number | null;
  cylinderMax: number | null;
  additionMin: number | null;
  additionMax: number | null;
  diameterMm: number | null;
  hasAntiReflective: boolean;
  hasBlueLightFilter: boolean;
  isPhotochromic: boolean;
  hasScratchResistant: boolean;
}

export interface ContactLensDetails {
  lensType: ContactLensType;
  replacement: ReplacementSchedule;
  baseCurve: number | null;
  diameterMm: number | null;
  power: number | null;
  cylinder: number | null;
  axis: number | null;
  addition: number | null;
  colorName: string | null;
  packSize: number;
  waterContentPercent: number | null;
}

export interface SolutionDetails {
  volumeMl: number;
  solutionType: string | null;
}

export interface ProductBarcodeSummary {
  id: string;
  code: string;
  type: BarcodeType;
  isPrimary: boolean;
  /** Set once the barcode is retired: it still resolves to this variant for history, never prints, and is never issued again. */
  retiredAt: string | null;
  createdAt: string;
}

/**
 * The photograph to show for a product or variant, as short-lived same-origin URLs.
 *
 * For a variant: its own primary photograph when it has one, otherwise the product's primary;
 * `source` says which. Null when neither exists, and the interface shows a placeholder.
 */
export interface ProductImage {
  thumbnailUrl: string;
  displayUrl: string;
  source: 'VARIANT' | 'PRODUCT';
}

/** One photograph in a product or variant gallery. `variantId` null is the product gallery. */
export interface ProductMediaSummary {
  id: string;
  variantId: string | null;
  role: ProductMediaRole;
  sortOrder: number;
  isPrimary: boolean;
  thumbnailUrl: string;
  displayUrl: string;
  width: number;
  height: number;
  createdAt: string;
}

export interface ProductVariantSummary {
  id: string;
  productId: string;
  sku: string;
  variantName: string | null;
  /** Whether movements of this variant must name a lot. */
  isBatchTracked: boolean;
  costPriceMinor: number | null;
  sellingPriceMinor: number;
  isActive: boolean;
  barcodes: ProductBarcodeSummary[];
  /** The variant's photograph, falling back to the product's. */
  image: ProductImage | null;
  frame: FrameDetails | null;
  prescriptionLens: PrescriptionLensDetails | null;
  contactLens: ContactLensDetails | null;
  solution: SolutionDetails | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductSummary {
  id: string;
  productNumber: string;
  type: ProductType;
  name: string;
  nameAr: string | null;
  description: string | null;
  status: ProductStatus;
  brandId: string | null;
  brandName: string | null;
  /** Tenant-defined merchandising grouping, separate from the technical `type`. */
  categoryId: string | null;
  categoryName: string | null;
  categoryNameAr: string | null;
  /** Frame and sunglasses only; null on other types and on frame-like products created before it existed. */
  lensInclusion: LensInclusion | null;
  variantCount: number;
  /** Lowest active selling price across variants, for list display. */
  fromPriceMinor: number | null;
  /** The product's primary photograph, if it has one. */
  image: ProductImage | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductDetail extends ProductSummary {
  variants: ProductVariantSummary[];
  /** Every photograph of the product and its variants, in gallery order. */
  media: ProductMediaSummary[];
}

// ---------------------------------------------------------------------------
// Barcode labels
// ---------------------------------------------------------------------------

/** How many labels one variant, and one print job, may ask for; a positive whole number either way. */
export const LABEL_MAX_PER_VARIANT = 500;
export const LABEL_MAX_PER_JOB = 2000;
/** How many variants one print job may name. */
export const LABEL_MAX_VARIANTS = 200;

export interface LabelRequest {
  items: { variantId: string; quantity: number }[];
  /** On by default; a label never carries cost. */
  showPrice: boolean;
  showSku: boolean;
}

/**
 * What one variant's label says, built by the server.
 *
 * The barcode is the variant's primary active code, drawn as Code 128 of exactly the stored string,
 * whatever its type — a scanner reads back the string the till looks up. The price is the selling
 * price in its own currency, never converted and never the cost. `variantId` is for the browser to
 * match a label to its line; it is never printed.
 */
export interface VariantLabel {
  variantId: string;
  quantity: number;
  productName: string;
  productNameAr: string | null;
  variantName: string | null;
  /** Short attributes worth reading off a shelf: colour, frame size. */
  details: string[];
  sku: string | null;
  barcode: { code: string; svg: string };
  price: { amountMinor: number; currencyCode: string } | null;
}

export interface LabelSheet {
  labels: VariantLabel[];
  totalLabels: number;
}

/** What a barcode scan resolves to. Shaped for the till, which needs one round trip. */
export interface BarcodeLookupResult {
  barcode: ProductBarcodeSummary;
  variant: ProductVariantSummary;
  product: ProductSummary;
}

/** Which detail shape a product type expects. Drives both validation and the form UI. */
export const PRODUCT_TYPE_DETAIL: Record<
  ProductType,
  'frame' | 'prescriptionLens' | 'contactLens' | 'solution' | null
> = {
  FRAME: 'frame',
  SUNGLASSES: 'frame',
  PRESCRIPTION_LENS: 'prescriptionLens',
  CONTACT_LENS: 'contactLens',
  SOLUTION: 'solution',
  ACCESSORY: null,
};
